import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Calendar, Plus, CheckCircle, X, ChevronRight, Search, Users } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import {
  safeAsync, Badge, emptyMsg, btnPrimary, btnSm, cardWrap,
  neuShadow, neuShadowSm, neuShadowInset, neuShadowPressed,
  APPT_STATUS_COLORS,
} from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

// Multi-doctor calendar coloring: deterministic hash → palette index so the
// same user always gets the same color across sessions. Avoids the color
// dancing around if user list order changes.
const HOST_PALETTE = [
  '#2E8B98', '#62BB46', '#F26522', '#B45309', '#7C3AED',
  '#0EA5E9', '#DC2626', '#15803D', '#9333EA', '#0F766E',
];
function hostColor(userId) {
  if (!userId) return '#94A3B8';
  let h = 0;
  const s = String(userId);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return HOST_PALETTE[Math.abs(h) % HOST_PALETTE.length];
}

const GROUP_COLORS = {
  today: '#62BB46',
  thisWeek: '#2E8B98',
  thisMonth: '#256F7A',
  upcoming: '#F26522',
  past: '#6C757D'
};

const GROUP_LABELS = {
  today: 'Today',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  upcoming: 'Upcoming',
  past: 'Past'
};

function getTimeGroup(startTime, endTime) {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(endTime);

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 86400000);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekEnd = new Date(weekStart.getTime() + 604800000);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  if (end < now) {
    return 'past';
  }
  if (start >= todayStart && start < todayEnd) {
    return 'today';
  }
  if (start >= weekStart && start < weekEnd) {
    return 'thisWeek';
  }
  if (start >= monthStart && start < monthEnd) {
    return 'thisMonth';
  }
  return 'upcoming';
}

function matchesSearch(appointment, searchTerm) {
  // All-tokens name match (card CRM-FULLNAME-SEARCH) — an empty term matches
  // everything, so the old `if (!searchTerm) return true` guard is built in.
  return matchesNameQuery(
    [appointment.title, appointment.first_name, appointment.last_name, appointment.location],
    searchTerm);
}

export default function CalendarTab({ appointments, contacts, canCreate, canUpdate, loadExtended }) {
  const appointmentsRef = useRef(null);
  const { onKeyDown: handleAppointmentsKeyDown } = useArrowNav(appointmentsRef, '[data-appointment-item]', { horizontal: false, vertical: true });

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set(['today', 'thisWeek']));

  // Filter appointments by search
  const filteredAppointments = useMemo(() => {
    return appointments.filter(a => matchesSearch(a, searchTerm));
  }, [appointments, searchTerm]);

  // Group and sort appointments
  const groupedAppointments = useMemo(() => {
    const groups = {
      today: [],
      thisWeek: [],
      thisMonth: [],
      upcoming: [],
      past: []
    };

    filteredAppointments.forEach(a => {
      const group = getTimeGroup(a.start_time, a.end_time);
      groups[group].push(a);
    });

    // Sort each group by start_time (ascending)
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    });

    return groups;
  }, [filteredAppointments]);

  const toggleGroup = (groupKey) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(groupKey)) {
      newSet.delete(groupKey);
    } else {
      newSet.add(groupKey);
    }
    setExpandedGroups(newSet);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(['today', 'thisWeek', 'thisMonth', 'upcoming', 'past']));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  // Auto-expand all groups when search is active
  React.useEffect(() => {
    if (searchTerm) {
      expandAll();
    }
  }, [searchTerm]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Calendar size={18} color="#4FA038" /> Appointments & Calendar
        </h3>
        {canCreate && (
          <button onClick={safeAsync(async () => {
            const contactId = prompt('Contact ID:');
            if (!contactId || isNaN(parseInt(contactId, 10))) {
              alert('Please enter a valid numeric Contact ID');
              return;
            }
            const title = prompt('Appointment title:');
            if (!title) return;
            const type = prompt('Type (consultation/follow_up/procedure/phone_call/video_call/meeting):', 'consultation');
            const dateStr = prompt('Date & time (YYYY-MM-DD HH:MM):', new Date().toISOString().slice(0, 16).replace('T', ' '));
            if (!dateStr) return;
            const startTime = new Date(dateStr).toISOString();
            const endTime = new Date(new Date(dateStr).getTime() + 3600000).toISOString();
            const location = prompt('Location (optional):') || '';
            await window.zignagenixAPI.createCRMAppointment({
              contact_id: parseInt(contactId, 10), title, appointment_type: type || 'consultation',
              start_time: startTime, end_time: endTime, location
            });
            loadExtended();
          })} style={btnPrimary}><Plus size={13} /> New Appointment</button>
        )}
      </div>

      {/* Status cards — neumorphic raised tiles */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        {['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show'].map(st => {
          const count = appointments.filter(a => a.status === st).length;
          const colors = { scheduled: '#2E8B98', confirmed: '#62BB46', completed: '#4FA038', cancelled: '#6C757D', no_show: '#DC3545', rescheduled: '#F26522' };
          return (
            <div
              key={st}
              style={{
                background: '#F8F9FA',
                borderRadius: 14,
                boxShadow: neuShadow,
                padding: '12px 22px',
                textAlign: 'center',
                minWidth: 88,
                transition: 'box-shadow 0.18s ease',
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: colors[st], lineHeight: 1.1 }}>{count}</div>
              <div style={{ fontSize: 9, color: '#6C757D', textTransform: 'capitalize', marginTop: 4, letterSpacing: 0.3 }}>{st.replace(/_/g, ' ')}</div>
            </div>
          );
        })}
      </div>

      {/* Search bar — inset neumorphic input */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={14} style={{ position: 'absolute', left: 14, color: '#6C757D', zIndex: 1 }} />
          <input
            type="text"
            placeholder="Search by title, contact, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: 36,
              paddingRight: 14,
              paddingTop: 10,
              paddingBottom: 10,
              borderRadius: 12,
              border: 'none',
              background: '#F8F9FA',
              boxShadow: neuShadowInset,
              fontSize: 12,
              fontFamily: 'inherit',
              outline: 'none',
              color: '#212529',
              transition: 'box-shadow 0.18s ease',
            }}
            onFocus={(e) => { e.target.style.boxShadow = neuShadowPressed; }}
            onBlur={(e) => { e.target.style.boxShadow = neuShadowInset; }}
          />
        </div>
      </div>

      {/* Expand/Collapse All buttons — small neumorphic raised buttons */}
      <div style={{ marginBottom: 14, display: 'flex', gap: 10 }}>
        {[
          { label: 'Expand All', onClick: expandAll },
          { label: 'Collapse All', onClick: collapseAll },
        ].map(({ label, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 10,
              border: 'none',
              background: '#F8F9FA',
              color: '#212529',
              boxShadow: neuShadowSm,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease, transform 0.05s ease',
            }}
            onMouseDown={(e) => { e.currentTarget.style.boxShadow = neuShadowPressed; }}
            onMouseUp={(e) => { e.currentTarget.style.boxShadow = neuShadowSm; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = neuShadowSm; }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Accordion groups */}
      <div ref={appointmentsRef} onKeyDown={handleAppointmentsKeyDown} style={cardWrap}>
        {Object.keys(GROUP_LABELS).map(groupKey => {
          const groupAppts = groupedAppointments[groupKey];
          const isExpanded = expandedGroups.has(groupKey);

          return (
            <div key={groupKey} style={{ marginBottom: 16 }}>
              {/* Group header — neumorphic raised pill with colored accent bar */}
              <button
                onClick={() => toggleGroup(groupKey)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: isExpanded ? '14px 14px 6px 6px' : 14,
                  border: 'none',
                  background: '#F8F9FA',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 13,
                  boxShadow: isExpanded ? neuShadowInset : neuShadow,
                  transition: 'box-shadow 0.18s ease, border-radius 0.2s ease',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                {/* Colored accent strip on the left, the only chromatic anchor */}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: 8,
                    top: 10,
                    bottom: 10,
                    width: 4,
                    borderRadius: 2,
                    background: GROUP_COLORS[groupKey],
                    boxShadow: `0 0 8px ${GROUP_COLORS[groupKey]}55`,
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 8 }}>
                  <ChevronRight
                    size={16}
                    color={GROUP_COLORS[groupKey]}
                    style={{
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <span>{GROUP_LABELS[groupKey]}</span>
                </div>
                {/* Count chip — small neumorphic inset bubble */}
                <div
                  style={{
                    background: '#F8F9FA',
                    borderRadius: 12,
                    padding: '3px 10px',
                    fontSize: 11,
                    fontWeight: 800,
                    color: GROUP_COLORS[groupKey],
                    minWidth: 28,
                    textAlign: 'center',
                    boxShadow: neuShadowInset,
                  }}
                >
                  {groupAppts.length}
                </div>
              </button>

              {/* Group content — inset neumorphic well, holds all rows */}
              <div
                style={{
                  maxHeight: isExpanded ? 1000 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                  background: '#F8F9FA',
                  borderRadius: '0 0 14px 14px',
                  boxShadow: isExpanded ? neuShadowInset : 'none',
                  padding: isExpanded ? '8px 10px' : '0 10px',
                  marginTop: isExpanded ? 4 : 0,
                }}
              >
                {groupAppts.length === 0 ? (
                  <div style={{ padding: '14px 18px', fontSize: 12, color: '#6C757D', fontStyle: 'italic' }}>
                    No appointments in this period.
                  </div>
                ) : (
                  groupAppts.map((a) => {
                    const statusColors = APPT_STATUS_COLORS;
                    const isPast = new Date(a.end_time) < new Date();
                    return (
                      <div
                        key={a.id}
                        data-appointment-item
                        tabIndex={0}
                        style={{
                          padding: '12px 14px',
                          margin: '8px 0',
                          borderRadius: 12,
                          background: '#F8F9FA',
                          boxShadow: neuShadowSm,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          opacity: isPast && a.status !== 'completed' ? 0.55 : 1,
                          outline: 'none',
                          transition: 'box-shadow 0.18s ease, opacity 0.2s ease',
                        }}
                        onFocus={(e) => { e.currentTarget.style.boxShadow = neuShadowInset; }}
                        onBlur={(e) => { e.currentTarget.style.boxShadow = neuShadowSm; }}
                        onMouseEnter={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.boxShadow = neuShadow; }}
                        onMouseLeave={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.boxShadow = neuShadowSm; }}
                      >
                        {/* Date block — small inset disc */}
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            background: '#F8F9FA',
                            boxShadow: neuShadowInset,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#212529', lineHeight: 1 }}>{new Date(a.start_time).getDate()}</div>
                          <div style={{ fontSize: 9, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{new Date(a.start_time).toLocaleDateString('en-US', { month: 'short' })}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>{a.title}</span>
                            <Badge text={a.status} color={statusColors[a.status] || '#6C757D'} />
                            <Badge text={(a.appointment_type || '').replace(/_/g, ' ')} color="#2E8B98" />
                          </div>
                          <div style={{ fontSize: 10, color: '#6C757D', marginTop: 4 }}>
                            {(a.first_name || a.last_name) ? `${a.first_name || ''} ${a.last_name || ''}`.trim() + ' · ' : ''}
                            {new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {new Date(a.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {a.location && ` · ${a.location}`}
                            {a.assigned_name && ` · ${a.assigned_name}`}
                          </div>
                        </div>
                        {canUpdate && a.status === 'scheduled' && (
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button onClick={safeAsync(async () => { await window.zignagenixAPI.updateCRMAppointment(a.id, { status: 'confirmed' }); loadExtended(); })} style={{ ...btnSm, padding: '5px 10px' }} title="Confirm"><CheckCircle size={12} /></button>
                            <button onClick={safeAsync(async () => { await window.zignagenixAPI.updateCRMAppointment(a.id, { status: 'cancelled' }); loadExtended(); })} style={{ ...btnSm, padding: '5px 10px', color: '#DC3545' }} title="Cancel"><X size={12} /></button>
                          </div>
                        )}
                        {canUpdate && a.status === 'confirmed' && (
                          <button onClick={safeAsync(async () => { await window.zignagenixAPI.updateCRMAppointment(a.id, { status: 'completed' }); loadExtended(); })} style={{ ...btnSm, padding: '5px 10px', flexShrink: 0 }} title="Complete"><CheckCircle size={12} color="#4FA038" /></button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}

        {filteredAppointments.length === 0 && (
          <div style={emptyMsg}>No appointments found.</div>
        )}
      </div>
    </div>
  );
}
